/**
 * The seeker's pipeline: every role they have saved or applied to, where it
 * stands, and their own notes.
 *
 * The rows are the same `applications` records auto-apply will write, so a
 * role the service applied to and one the seeker applied to by hand sit in
 * one list and move through one set of stages.
 *
 * Loading, empty and failed are three different screens. Every field saves
 * on its own and reports its own failure on the row, because a form that
 * says nothing when a save fails has taught this codebase enough already.
 */

import { useCallback, useEffect, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { FillHint } from "./FillHint";
import { Send } from "./Send";
import { SendTag } from "./SendTag";
import type { ExtensionStatus } from "./extension";
import { People, type ContactView } from "./People";
import { api, ApiError } from "./auth";
import { PostingReader } from "./Posting";
import { ConfirmRemove } from "./fields";
import { InterviewPrep } from "./InterviewPrep";
import { useCloudRun, CloudSendButton } from "./AutoRun";

type Stage = "saved" | "applied" | "screening" | "interviewing" | "offer" | "rejected" | "closed";

interface ApplicationView {
  applicationId: string;
  jobId: string;
  stage: Stage;
  notes: string | null;
  followUpOn: string | null;
  appliedAt: string | null;
  createdAt: string;
  updatedAt: string;
  submission: {
    mode: "manual" | "review" | "auto";
    state: "none" | "queued" | "in_progress" | "submitted" | "failed" | "needs_input" | "abandoned";
    submittedAt: string | null;
    failureCode: string | null;
    failureDetail: string | null;
    attempts: number;
  };
  job: {
    title: string;
    employer: string;
    locationMetro: string | null;
    place: string | null;
    isRemote: boolean;
    postedAt: string | null;
    closedAt: string | null;
    applyUrl: string | null;
    comp: { minCents: number | null; maxCents: number | null } | null;
  };
  people: ContactView[];
}

interface Funnel {
  reached: Array<{ stage: Stage; label: string; count: number }>;
  outcomes: { inPlay: number; offers: number; rejected: number; closed: number; saved: number };
  waiting: { count: number; oldestDays: number | null; medianDays: number | null };
  byWeek: Array<{ weekOf: string; count: number }>;
}

interface ApplicationsResponse {
  applications: ApplicationView[];
  counts: Record<Stage, number>;
  funnel: Funnel;
  stages: Array<{ key: Stage; label: string }>;
}

type Bucket = "in-play" | "saved" | "over";

const BUCKET_OF: Record<Stage, Bucket> = {
  offer: "in-play", interviewing: "in-play", screening: "in-play", applied: "in-play",
  saved: "saved",
  rejected: "over", closed: "over",
};

const BUCKET_TITLE: Record<Bucket, string> = {
  "in-play": "In play",
  saved: "Saved for later",
  over: "Over",
};

/** Same order the API uses, so a stage change moves the row where it belongs. */
const RANK: Record<Stage, number> = {
  offer: 0, interviewing: 1, screening: 2, applied: 3, saved: 4, rejected: 5, closed: 6,
};

function sortForPipeline(list: ApplicationView[]): ApplicationView[] {
  return [...list].sort((a, b) =>
    RANK[a.stage] - RANK[b.stage] || Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
}

function money(cents: number | null): string | null {
  if (cents == null) return null;
  return `$${Math.round(cents / 100_000).toLocaleString()}k`;
}

function payLabel(comp: ApplicationView["job"]["comp"]): string | null {
  if (!comp) return null;
  const lo = money(comp.minCents);
  const hi = money(comp.maxCents);
  if (lo && hi && lo !== hi) return `${lo}–${hi}`;
  return lo ?? hi;
}

function daysAgo(iso: string | null): string | null {
  if (!iso) return null;
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return null;
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "a month ago" : `${months} months ago`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function describe(err: unknown): string {
  return err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err);
}

export default function Applications({
  onBack,
  onOpenMatches,
  onTailor,
  autoMode = "review",
  ext = null,
  focusJobId = null,
  onFocused,
  onAnswerLeft,
}: {
  onBack: () => void;
  onOpenMatches: () => void;
  onTailor: (jobId: string) => void;
  /** Whether Auto is on: decides what a stopped application offers next (E16). */
  autoMode?: "auto" | "review";
  /** The Chrome extension on this page, for saying where the fill happens beside each posting. */
  ext?: ExtensionStatus | null;
  /** Open scrolled to this application's row, lit up: the one a cloud run just stopped on, or a follow-up that came due. */
  focusJobId?: string | null;
  /** Called once the row has been shown, so the focus is not re-applied on the next visit. */
  onFocused?: () => void;
  /** Open one application's open questions, and nothing else (AnswerLeft). */
  onAnswerLeft?: (jobId: string) => void;
}) {
  const [data, setData] = useState<ApplicationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The list is compact (17 Sept): one two-line row per application, and the
  // row the person clicks opens in place to everything about it. One open at
  // a time, so the page stays a list; the row the person came for (a cloud
  // run's stop, a follow-up due, back from "Answer what's left") opens itself.
  const [openId, setOpenId] = useState<string | null>(focusJobId ?? null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api<ApplicationsResponse>("/api/applications")
      .then(setData)
      .catch((err) => setError(describe(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // The row the person came for: scrolled into view and lit for a moment once
  // the list has rendered. Cleared after, so a later visit starts at the top.
  useEffect(() => {
    if (!focusJobId || !data) return;
    setOpenId(focusJobId);
    const el = document.getElementById(`application-${focusJobId}`);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.classList.add("focused");
    // Not cancelled on cleanup: the parent clears the focus the moment this
    // runs, and a cancelled timer would leave the row lit for good.
    setTimeout(() => el.classList.remove("focused"), 4_000);
    onFocused?.();
  }, [focusJobId, data, onFocused]);

  /** The list again, quietly: after a cloud run changed a row on the server. */
  const refresh = useCallback(() => {
    api<ApplicationsResponse>("/api/applications").then(setData).catch(() => { /* the row keeps what it showed */ });
  }, []);

  /** One row changed on the server; keep the list in pipeline order. */
  const replace = (updated: ApplicationView) =>
    setData((d) => d && {
      ...d,
      applications: sortForPipeline(d.applications.map((a) => (a.jobId === updated.jobId ? updated : a))),
    });

  const drop = (jobId: string) =>
    setData((d) => d && { ...d, applications: d.applications.filter((a) => a.jobId !== jobId) });

  if (loading) return <p className="muted">Loading your pipeline…</p>;

  if (error) {
    return (
      <section>
        <h1>Applications</h1>
        <p className="err">{error}</p>
        <div className="row">
          <button className="btn" onClick={load}>Try again</button>
          <button className="btn ghost" onClick={onBack}>Back</button>
        </div>
      </section>
    );
  }

  if (!data) return null;

  const inPlay = data.applications.filter((a) => BUCKET_OF[a.stage] === "in-play").length;
  const saved = data.applications.filter((a) => BUCKET_OF[a.stage] === "saved").length;
  const over = data.applications.filter((a) => BUCKET_OF[a.stage] === "over").length;
  const due = data.applications.filter((a) => a.followUpOn && a.followUpOn <= todayIso() && BUCKET_OF[a.stage] !== "over").length;
  // Stopped on a question JobCork could not answer from the confirmed record
  // (E14): these come first, whatever their stage, because they are the ones
  // waiting on the person.
  const stale = Date.now() - 10 * 60_000;
  const needsYou = data.applications.filter((a) =>
    (a.submission.state === "needs_input" || (a.submission.state === "in_progress" && a.submission.mode === "auto" && Date.parse(a.updatedAt) < stale))
    && BUCKET_OF[a.stage] !== "over");
  const needsYouIds = new Set(needsYou.map((a) => a.jobId));

  return (
    <section>
      <header className="bar">
        <div>
          <h1 className="tight">Applications</h1>
          <p className="muted small">
            {data.applications.length === 0
              ? "Nothing tracked yet"
              : [`${inPlay} in play`, `${saved} saved`, `${over} over`].join(" · ")}
            {due > 0 ? ` · ${due} follow-up${due === 1 ? "" : "s"} due` : ""}
            {needsYou.length > 0 ? ` · ${needsYou.length} need${needsYou.length === 1 ? "s" : ""} you` : ""}
          </p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      {data.applications.length === 0 && (
        <div className="notice">
          <p>Nothing here yet.</p>
          <p className="muted small">
            On any match, <strong>Save for later</strong> or <strong>I applied</strong> puts
            it here. Then track it through screening, interviews and the answer.
          </p>
          <div className="row">
            <button className="btn" onClick={onOpenMatches}>See ranked matches</button>
          </div>
        </div>
      )}

      {data.funnel.reached[1].count > 0 && <FunnelView funnel={data.funnel} />}

      {needsYou.length > 0 && (
        <div>
          <h2>Needs you <span className="muted">· {needsYou.length}</span></h2>
          <p className="muted small">
            JobCork filled what it could and stopped: a required question it will not answer for you.
            Open the form, answer it, press Submit — JobCork records the rest.
          </p>
          {autoMode === "auto" && (
            <RetryAll rows={needsYou} onDone={refresh} />
          )}
          <ol className="matches">
            {needsYou.map((a) => (
              <ApplicationRow
                key={a.applicationId}
                app={a}
                stages={data.stages}
                onChanged={replace}
                onRemoved={() => drop(a.jobId)}
                onTailor={() => onTailor(a.jobId)}
                autoMode={autoMode}
                ext={ext}
                onRefresh={refresh}
                onAnswerLeft={onAnswerLeft}
                open={openId === a.jobId}
                onToggle={() => setOpenId(openId === a.jobId ? null : a.jobId)}
              />
            ))}
          </ol>
        </div>
      )}

      {(["in-play", "saved", "over"] as Bucket[]).map((bucket) => {
        const rows = data.applications.filter((a) => BUCKET_OF[a.stage] === bucket && !needsYouIds.has(a.jobId));
        if (rows.length === 0) return null;
        return (
          <div key={bucket}>
            <h2>{BUCKET_TITLE[bucket]} <span className="muted">· {rows.length}</span></h2>
            <ol className="matches">
              {rows.map((a) => (
                <ApplicationRow
                  key={a.applicationId}
                  app={a}
                  stages={data.stages}
                  onChanged={replace}
                  onRemoved={() => drop(a.jobId)}
                  onTailor={() => onTailor(a.jobId)}
                  autoMode={autoMode}
                ext={ext}
                  onRefresh={refresh}
                  onAnswerLeft={onAnswerLeft}
                  open={openId === a.jobId}
                  onToggle={() => setOpenId(openId === a.jobId ? null : a.jobId)}
                />
              ))}
            </ol>
          </div>
        );
      })}
    </section>
  );
}

/**
 * The seeker's numbers. Reached-at-least along the path, so a rejection after
 * an interview still counts the interview; silence counted honestly, because
 * "how many have not answered me" is the number every other dashboard hides;
 * and what went out each week, so a slow fortnight is visible as one.
 */
function FunnelView({ funnel }: { funnel: Funnel }) {
  const path = funnel.reached.slice(1); // applied onward; "saved" is not a step in the hunt
  const top = Math.max(1, path[0].count);
  const weeksMax = Math.max(1, ...funnel.byWeek.map((w) => w.count));
  const total = funnel.byWeek.reduce((n, w) => n + w.count, 0);
  const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 100)}%` : "—");
  const days = (n: number) => `${n} day${n === 1 ? "" : "s"}`;
  const weekLabel = (iso: string) => {
    const d = new Date(iso + "T00:00:00Z");
    return `${d.getUTCDate()} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()]}`;
  };

  return (
    <section className="funnel">
      <h2>Your numbers</h2>

      <ol className="funnel-steps" aria-label="How far your applications have got">
        {path.map((step, i) => (
          <li key={step.stage} className="funnel-step" title={`${step.count} reached ${step.label.toLowerCase()}`}>
            <span className="funnel-label">{step.label}</span>
            <span className="funnel-track"><span className="funnel-bar" style={{ width: `${(step.count / top) * 100}%` }} /></span>
            <span className="funnel-count"><span className="ink">{step.count}</span>{i > 0 && <span className="muted small"> · {pct(step.count, path[i - 1].count)} of {path[i - 1].label.toLowerCase()}</span>}</span>
          </li>
        ))}
      </ol>

      <p className="small">
        {funnel.waiting.count === 0
          ? <>Nothing is waiting on an answer.</>
          : <><strong>{funnel.waiting.count} sent and still unanswered</strong> — the oldest for {days(funnel.waiting.oldestDays ?? 0)}{funnel.waiting.count > 1 && funnel.waiting.medianDays !== null ? `, typically ${days(funnel.waiting.medianDays)}` : ""}.</>}
        {" "}{funnel.outcomes.rejected > 0 || funnel.outcomes.closed > 0
          ? `${funnel.outcomes.rejected} rejected, ${funnel.outcomes.closed} closed.`
          : ""}
      </p>

      <div className="weeks" role="img" aria-label={`Applications sent per week, last ${funnel.byWeek.length} weeks: ${total} in all`}>
        {funnel.byWeek.map((w) => (
          <div key={w.weekOf} className="week" title={`Week of ${weekLabel(w.weekOf)}: ${w.count} sent`}>
            <span className="week-count small">{w.count > 0 ? w.count : ""}</span>
            <span className="week-bar" style={{ height: `${Math.max(w.count > 0 ? 6 : 2, (w.count / weeksMax) * 64)}px` }} />
          </div>
        ))}
      </div>
      <p className="muted small">Sent per week, {weekLabel(funnel.byWeek[0].weekOf)} to now — {total} in the last {funnel.byWeek.length} weeks.</p>
    </section>
  );
}

interface AuditEvent {
  seq: number;
  kind: string;
  fieldLabel: string | null;
  fieldValue: string | null;
  valueSource: string | null;
  isSensitive: boolean;
  note: string | null;
  occurredAt: string;
  /** The server's one-line reading of the event. */
  text: string;
}

/** How long a fill may stay "in progress" before the record asks the person what happened. */
const UNCONFIRMED_AFTER_MS = 10 * 60_000;

const STATE_LABEL: Record<ApplicationView["submission"]["state"], string> = {
  none: "",
  queued: "Queued — Auto will try it on its next pass",
  in_progress: "JobCork is filling the form",
  submitted: "Sent by JobCork",
  failed: "Failed — nothing was sent",
  needs_input: "Stopped — needs you",
  abandoned: "Skipped",
};

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

/** "Left for you: A (why); B (why)" as items, so a long stop reads as a list. */
export function leftForYou(note: string | null): string[] | null {
  const m = /^Left for you:\s*(.+)$/s.exec(note ?? "");
  if (!m) return null;
  // Items are joined with "; "; a semicolon inside an item's own "(why)" is not a join ("Your resume does not plainly show this; answer it yourself." used to split into two items).
  return m[1].split(/;\s+(?![^(]*\))/).map((x) => x.trim()).filter(Boolean);
}

/** "How did you learn about this job? (A question the form asks that JobCork does not answer.)" -> the question and the why. */
export function splitLeft(item: string): { question: string; why: string | null } {
  const m = /^(.*?)\s*\(([^()]*(?:\([^()]*\)[^()]*)*)\)\s*$/.exec(item);
  return m ? { question: m[1].trim(), why: m[2].trim() } : { question: item, why: null };
}

/** The questions a saved answer can never take: the fill leaves these to the person on every form. */
export const NOT_BANKABLE = /\b(felony|convict|criminal|arrest|background check|gender|race|ethnic|veteran|disabilit|hispanic|latino|signature|cover letter|resume|social security)\b/i;

/**
 * A question JobCork left, with a place to answer it for next time (the
 * question bank, E15): saved here, it is given on every later form that
 * asks the same thing. The wording keeps the employer's name; the key does not.
 */
/** A question that wants a paragraph about this employer: answered for this posting only, and worth drafting. */
const ESSAY_LIKE = /\b(why|describe|tell us|tell me|explain|what interests|what excites|what draws|what makes you|how would you|how did you|in your own words|anything else|cover letter|motivation)\b/i;

export function LeftItem({ item, employer, jobId, onSaved, autoMode, openAtOnce = false }: { item: string; employer: string; jobId: string; onSaved: (question: string) => void; autoMode: "auto" | "review"; openAtOnce?: boolean }) {
  const { question, why } = splitLeft(item);
  const essay = ESSAY_LIKE.test(question);
  const [open, setOpen] = useState(openAtOnce);
  const [text, setText] = useState("");
  const [scope, setScope] = useState<"posting" | "all">(essay ? "posting" : "all");
  const [busy, setBusy] = useState<"save" | "draft" | null>(null);
  const [saved, setSaved] = useState<"posting" | "all" | null>(null);
  const [flags, setFlags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const bankable = question.length >= 4 && !NOT_BANKABLE.test(question) && !/not in your answers|Add it in JobCork|not answered in your profile/i.test(why ?? "");

  const save = async () => {
    if (!text.trim()) return;
    setBusy("save");
    setError(null);
    try {
      await api("/api/questions/answers", { method: "PUT", body: JSON.stringify({ question, employer, answers: [text.trim()], ...(scope === "posting" ? { jobId } : {}) }) });
      setSaved(scope);
      setOpen(false);
      onSaved(question);
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(null);
    }
  };

  // A first draft from the resume and this posting, by the model -- checked
  // for claims the resume does not support, and yours to read and change
  // before it is saved. Nothing goes out unread.
  const draft = async () => {
    setBusy("draft");
    setError(null);
    setFlags([]);
    try {
      const r = await api<{ drafts: Array<{ id: string; text: string; reason?: string | null; flags: string[] }> }>("/api/apply/answers", {
        method: "POST", body: JSON.stringify({ jobId, questions: [{ id: "q", label: question }] }),
      });
      const d = r.drafts[0];
      if (!d || !d.text) { setError(d?.reason ?? "The model did not draft an answer."); return; }
      setText(d.text);
      setFlags(d.flags ?? []);
      setScope("posting");
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <li>
      {item}
      {bankable && !saved && (
        <>
          {" "}
          <button className="step-link small" onClick={() => setOpen(!open)}>{open ? "Cancel" : "Answer this for next time"}</button>
          {open && (
            <div className="left-answer">
              {essay ? (
                <textarea className="input" rows={4} value={text} placeholder="Your answer, in your words" disabled={busy !== null}
                          onChange={(e) => setText(e.target.value)} />
              ) : (
                <input className="input" value={text} placeholder="Your answer, in your words" disabled={busy !== null}
                       onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void save(); }} />
              )}
              {flags.length > 0 && (
                <p className="err small">The draft claims what your resume does not say: {flags.join("; ")}. Change or remove those before saving.</p>
              )}
              <div className="row">
                <label className="choice small"><input type="radio" name={`scope-${jobId}-${question.slice(0, 20)}`} checked={scope === "posting"} onChange={() => setScope("posting")} /> <span>Only this posting</span></label>
                <label className="choice small"><input type="radio" name={`scope-${jobId}-${question.slice(0, 20)}`} checked={scope === "all"} onChange={() => setScope("all")} /> <span>Every form that asks this</span></label>
              </div>
              <div className="row">
                <button className="btn small" disabled={busy !== null || !text.trim()} onClick={save}>{busy === "save" ? "Saving…" : "Save"}</button>
                {essay && <button className="btn ghost small" disabled={busy !== null} onClick={draft}>{busy === "draft" ? "Drafting…" : "Draft it for me"}</button>}
              </div>
              {essay && <p className="muted small">A draft comes from your resume and this posting, with anything your resume does not support named. It is saved only when you press Save.</p>}
            </div>
          )}
          {error && <span className="err small"> {error}</span>}
        </>
      )}
      {saved === "all" && <span className="ok small"> Saved — every later form that asks this gets your answer.{autoMode === "auto" ? " Auto will try this one again." : ""}</span>}
      {saved === "posting" && <span className="ok small"> Saved for this posting only.{autoMode === "auto" ? " Auto will try this one again." : " Open the posting and JobCork fills it in."}</span>}
    </li>
  );
}

/**
 * What JobCork did with this application, and -- on request -- every field,
 * every answer and every file it put on the form, with where each value came
 * from. Failure is shown as plainly as success, because silent failure is the
 * category's first complaint.
 */
/**
 * The drafted follow-up, and sending it (post-application). Once a role is in
 * play, JobCork writes a short note from the person's resume and this role,
 * checked so it claims nothing the resume does not show. The person reads it,
 * edits whatever they like, and presses Send — and it goes from their own
 * JobCork application address, so the recruiter's reply comes back to the same
 * place, files itself against this application, and reaches their inbox.
 *
 * Nothing goes out without the person seeing the words and confirming the
 * recipient. The draft itself is not stored; each press redrafts.
 */
interface RecipientOption {
  address: string;
  /** Who this is, in plain words: "Jane Doe — recruiter", "Sam Patel — wrote to you Sep 5". */
  label: string;
  from: "contact" | "reply" | "posting";
}
interface FollowUpDraftData {
  subject: string;
  body: string;
  flags: Array<{ claim: string }>;
  /** The relay address it will go out from, when one could be minted. */
  from: string | null;
  /** Addresses JobCork actually holds for this application. Never a guess. */
  recipients: RecipientOption[];
}

function FollowUpDraft({ jobId, people, onSent }: { jobId: string; people: ContactView[]; onSent: (followUpOn: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<FollowUpDraftData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(() => people.find((p) => p.email)?.email ?? "");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ to: string; followUpOn: string } | null>(null);

  const draft = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    setSent(null);
    try {
      const r = await api<FollowUpDraftData>(`/api/applications/${jobId}/followup`, { method: "POST", body: "{}" });
      setNote(r);
      setOpen(true);
      if (!to) setTo(r.recipients?.[0]?.address ?? people.find((p) => p.email)?.email ?? "");
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };
  const copy = () => {
    if (!note) return;
    navigator.clipboard?.writeText(`Subject: ${note.subject}\n\n${note.body}`).then(() => setCopied(true)).catch(() => setCopied(false));
  };
  const send = async () => {
    if (!note) return;
    setSending(true);
    setError(null);
    try {
      const r = await api<{ to: string; followUpOn: string }>(`/api/applications/${jobId}/followup/send`, {
        method: "POST",
        body: JSON.stringify({ to, subject: note.subject, body: note.body }),
      });
      setSent({ to: r.to, followUpOn: r.followUpOn });
      setConfirming(false);
      setOpen(false);
      onSent(r.followUpOn);
    } catch (e) {
      setError(describe(e));
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="followup">
      {!open && !sent && (
        <button className="btn ghost small" type="button" disabled={busy} onClick={() => void draft()}>
          {busy ? "Drafting…" : note ? "Show the follow-up" : "Draft a follow-up"}
        </button>
      )}
      {sent && (
        <p className="ok small nomargin">
          Sent to {sent.to}. Their reply comes back to your JobCork address and files itself here — next nudge due {sent.followUpOn}.
        </p>
      )}
      {error && <p className="err small">{error}</p>}
      {open && note && (
        <div className="followup-draft">
          <p className="muted small">
            A note to the recruiter, in your words. JobCork writes only what your resume backs; edit anything before it goes.
          </p>
          <label className="field-label" htmlFor={`fu-to-${jobId}`}>To</label>
          <input
            id={`fu-to-${jobId}`}
            className="input"
            type="email"
            placeholder="recruiter@company.com"
            value={to}
            onChange={(e) => { setTo(e.target.value); setConfirming(false); }}
          />
          {(note.recipients ?? []).length > 0 ? (
            <div className="to-suggest">
              {(note.recipients ?? []).map((r) => (
                <button
                  key={r.address}
                  type="button"
                  className={`chip${to.trim().toLowerCase() === r.address ? " on" : ""}`}
                  onClick={() => { setTo(r.address); setConfirming(false); }}
                  title={r.address}
                >
                  {r.label}
                </button>
              ))}
            </div>
          ) : (
            <p className="field-hint muted small">
              JobCork doesn't have an address for this one. It applied through the employer's form, and nobody there
              has written to you yet — an ATS no-reply isn't a person, so there is nothing to write back to. When
              someone does reply it lands at your JobCork address and appears here as one press. Until then: find the
              recruiter on LinkedIn, or use an address the company publishes — and save them under People above so
              this fills itself in.
            </p>
          )}
          <label className="field-label" htmlFor={`fu-subj-${jobId}`}>Subject</label>
          <input id={`fu-subj-${jobId}`} className="input" value={note.subject} onChange={(e) => { setNote({ ...note, subject: e.target.value }); setConfirming(false); }} />
          <label className="field-label" htmlFor={`fu-body-${jobId}`}>Message</label>
          <textarea id={`fu-body-${jobId}`} className="input notes" rows={7} value={note.body} onChange={(e) => { setNote({ ...note, body: e.target.value }); setConfirming(false); }} />
          {note.flags.length > 0 && (
            <p className="err small">Read before sending — not in your resume: {[...new Set(note.flags.map((f) => f.claim))].join(", ")}.</p>
          )}
          {confirming ? (
            <div className="followup-confirm">
              <p className="small nomargin">
                Send to <strong>{to}</strong>{note.from ? <> as <strong>{note.from}</strong></> : null}? It goes immediately, in your name.
              </p>
              <div className="row">
                <button className="btn small" type="button" disabled={sending} onClick={() => void send()}>{sending ? "Sending…" : "Yes, send it"}</button>
                <button className="step-link small" type="button" disabled={sending} onClick={() => setConfirming(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="row">
              <button className="btn small" type="button" disabled={!to.trim() || !note.body.trim()} onClick={() => setConfirming(true)}>Send it</button>
              <button className="step-link small" type="button" onClick={copy}>{copied ? "Copied" : "Copy instead"}</button>
              <button className="step-link small" type="button" disabled={busy} onClick={() => void draft()}>{busy ? "Redrafting…" : "Redraft"}</button>
              <button className="step-link small" type="button" onClick={() => setOpen(false)}>Hide</button>
            </div>
          )}
          {note.from && !confirming && (
            <p className="field-hint muted small">Goes from {note.from} — their reply lands in your inbox and updates this application on its own.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Submission({ app, autoMode, onChanged, onRefresh, onAnswerLeft }: { app: ApplicationView; autoMode: "auto" | "review"; onChanged: (a: ApplicationView) => void; onRefresh: () => void; onAnswerLeft?: (jobId: string) => void }) {
  const s = app.submission;
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [open, setOpen] = useState(false);
  const [showSensitive, setShowSensitive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requeueBusy, setRequeueBusy] = useState(false);
  const [requeueError, setRequeueError] = useState<string | null>(null);
  const [settleBusy, setSettleBusy] = useState<"sent" | "not_sent" | null>(null);
  const [settleError, setSettleError] = useState<string | null>(null);
  const cloud = useCloudRun(onRefresh);

  // The second pass (E16): a stopped, failed or skipped application goes
  // back in Auto's queue -- when the person answers what stopped it, or asks.
  const requeue = async (note?: string): Promise<boolean> => {
    setRequeueBusy(true);
    setRequeueError(null);
    try {
      const r = await api<{ state: string; changed: boolean; application?: ApplicationView }>(`/api/applications/${app.jobId}/requeue`, {
        method: "POST", body: JSON.stringify(note ? { note } : {}),
      });
      if (r.application) onChanged(r.application);
      // The record is fresh; what was sent is read again when asked for.
      setEvents(null);
      return true;
    } catch (e) {
      setRequeueError(describe(e));
      return false;
    } finally {
      setRequeueBusy(false);
    }
  };
  const answered = (question: string) => { if (autoMode === "auto") void requeue(`after you answered “${question}”`); };
  // Settling (E8): the person's word on a record JobCork could not finish reading.
  const settle = async (outcome: "sent" | "not_sent") => {
    setSettleBusy(outcome);
    setSettleError(null);
    try {
      const r = await api<{ state: string; application?: ApplicationView }>(`/api/applications/${app.jobId}/settle`, {
        method: "POST", body: JSON.stringify({ outcome }),
      });
      if (r.application) onChanged(r.application);
      setEvents(null);
    } catch (e) {
      setSettleError(describe(e));
    } finally {
      setSettleBusy(null);
    }
  };
  const sendNow = async () => {
    if (await requeue("to send it now")) void cloud.start(false, app.jobId);
  };

  const toggle = () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (events !== null) return;
    api<{ events: AuditEvent[] }>(`/api/applications/${app.jobId}/events`)
      .then((r) => setEvents(r.events))
      .catch((err) => setError(describe(err)));
  };

  // A fill that never reached its end (E8): the page moved on where nothing
  // was watching, the tab closed, the form said nothing JobCork knew. After
  // a while the record stops claiming JobCork is still at it and asks the
  // person, who can settle it either way.
  const unconfirmed = s.state === "in_progress" && Date.parse(app.updatedAt) < Date.now() - UNCONFIRMED_AFTER_MS;
  // A cloud send that filled everything then hit an "I'm not a robot" check.
  // Sending again from the cloud would only hit it again; the answer is to
  // finish it in Chrome, where the person's own browser clears the check.
  const humanCheck = s.state === "needs_input" && /human check|not a robot|captcha/i.test(s.failureDetail ?? "");
  // The employer's form refused a send JobCork pressed as spam -- this one, or
  // an earlier one at the same employer (a hand-off). Sending again by machine
  // would only be refused again; the person presses Submit in Chrome.
  const flagged = s.state === "needs_input" && !humanCheck && /possible spam|calling it spam/i.test(s.failureDetail ?? "");
  const tone = s.state === "submitted" ? "badge" : s.state === "failed" || s.state === "needs_input" || unconfirmed ? "badge due" : "badge muted-badge";
  const items = s.state === "needs_input" ? leftForYou(s.failureDetail) : null;
  const detail = s.state === "submitted" ? when(s.submittedAt) : items ? "" : unconfirmed
    ? (s.mode === "auto"
        ? "Auto pressed Submit and the page did not confirm it went through. Check your email; if it did, say so here."
        : "JobCork filled this form and did not see the page confirm it went through. If you pressed Submit and the page moved on, it may well have. Check your email, then say which here.")
    : s.failureDetail ?? "";
  const canSettle = !app.job.closedAt && (unconfirmed || s.state === "needs_input" || s.state === "failed");

  return (
    <div className="submission">
      <p className={tone}>{unconfirmed ? "Sent? — unconfirmed" : s.state === "queued" && autoMode !== "auto" ? (s.mode === "auto" ? "Ready to send again from the cloud" : "Queued for Auto — waiting, since Auto is off") : STATE_LABEL[s.state]}{detail && s.state === "submitted" ? ` · ${detail}` : ""}</p>
      {detail && s.state !== "submitted" && <p className="err small">{detail}</p>}
      {canSettle && (
        <div className="settle small">
          <div className="row">
            <button className={`btn small${unconfirmed ? "" : " ghost"}`} type="button" disabled={settleBusy !== null} onClick={() => void settle("sent")}>
              {settleBusy === "sent" ? "Marking…" : unconfirmed ? "It was sent" : "I sent it myself"}
            </button>
            {unconfirmed && (
              <button className="btn ghost small" type="button" disabled={settleBusy !== null} onClick={() => void settle("not_sent")}>
                {settleBusy === "not_sent" ? "Marking…" : "Nothing was sent"}
              </button>
            )}
          </div>
          {settleError && <p className="err small">{settleError}</p>}
        </div>
      )}
      {items && onAnswerLeft && (
        <p className="small nomargin"><button className="btn small" onClick={() => onAnswerLeft(app.jobId)}>Answer what's left ({items.length})</button> <span className="muted">— just this application's questions, on a page of their own.</span></p>
      )}
      {items && (
        <ul className="left-for-you small">
          {items.map((it) => <LeftItem key={it} item={it} employer={app.job.employer} jobId={app.jobId} autoMode={autoMode} onSaved={answered} />)}
        </ul>
      )}
      {humanCheck && !app.job.closedAt && app.job.applyUrl && (
        <div className="second-pass small finish-chrome">
          <p className="muted small">
            JobCork filled every field from the cloud, then hit an “I'm not a robot” check — it never answers those for you. Open it in Chrome: JobCork fills the form again with your saved answers, you clear the box and press Submit.
          </p>
          <div className="row">
            <a className="btn small" href={app.job.applyUrl} target="_blank" rel="noreferrer noopener">Finish in Chrome →</a>
          </div>
        </div>
      )}
      {flagged && !app.job.closedAt && app.job.applyUrl && (
        <div className="second-pass small finish-chrome">
          <p className="muted small">
            {app.job.employer}'s form refuses sends JobCork presses itself, calling them spam. Open it in Chrome: JobCork fills the form with your saved answers and leaves the Submit button to you — one click, and JobCork records what the page says.
          </p>
          <div className="row">
            <a className="btn small" href={app.job.applyUrl} target="_blank" rel="noreferrer noopener">Finish in Chrome →</a>
          </div>
        </div>
      )}
      {!humanCheck && !flagged && (s.state === "needs_input" || s.state === "failed" || s.state === "abandoned") && !app.job.closedAt && (
        autoMode === "auto" || s.mode === "auto" ? (
          <div className="second-pass small">
            <div className="row">
              {autoMode === "auto" && (
                <button className="btn ghost small" type="button" disabled={requeueBusy || cloud.busy !== null} onClick={() => void requeue()}>
                  {requeueBusy ? "Queuing…" : "Try again on Auto's next pass"}
                </button>
              )}
              <CloudSendButton
                onSend={() => void sendNow()}
                busy={cloud.busy === "send"}
                disabled={requeueBusy || cloud.busy !== null}
                idle={autoMode === "auto" ? "Send it now" : "Send it again from the cloud"}
                note={`This sends a real application to ${app.job.employer} now.`}
                className={`${autoMode === "auto" ? "step-link" : "btn ghost"} small`}
              />
            </div>
            {cloud.line && <p className={`small ${cloud.line.ok ? "ok" : "muted"}`}>{cloud.line.text}</p>}
            {(requeueError ?? cloud.error) && <p className="err small">{requeueError ?? cloud.error}</p>}
          </div>
        ) : (
          <p className="muted small second-pass">
            {s.state === "needs_input" ? "Answered it? Open the posting and JobCork fills the form again, with your saved answers; you press Submit." : "Open the posting to try it again yourself; JobCork fills what it can."}
          </p>
        )
      )}
      {s.state === "queued" && s.attempts > 0 && autoMode === "auto" && (
        <p className="muted small second-pass">Back in the queue. Auto opens it again on its next pass, with everything you have answered since.</p>
      )}
      {s.state === "queued" && autoMode !== "auto" && s.mode === "auto" && !app.job.closedAt && (
        <div className="second-pass small">
          <div className="row">
            <CloudSendButton
              onSend={() => void cloud.start(false, app.jobId)}
              busy={cloud.busy === "send"}
              disabled={cloud.busy !== null}
              idle="Send it again from the cloud"
              note={`This sends a real application to ${app.job.employer} now.`}
            />
          </div>
          {cloud.line && <p className={`small ${cloud.line.ok ? "ok" : "muted"}`}>{cloud.line.text}</p>}
          {cloud.error && <p className="err small">{cloud.error}</p>}
        </div>
      )}
      <p className="small">
        <button className="step-link small" type="button" onClick={toggle}>
          {open ? "Hide what was sent" : "What was sent"}
        </button>
        {s.attempts > 1 && <span className="muted"> · {s.attempts} attempts</span>}
      </p>
      {open && error && <p className="err small">{error}</p>}
      {open && events === null && !error && <p className="muted small">Loading the record…</p>}
      {open && events !== null && events.length === 0 && <p className="muted small">Nothing was recorded for this application.</p>}
      {open && events !== null && events.length > 0 && (
        <ol className="audit">
          {events.map((e) => (
            <li key={e.seq} className={`audit-${e.kind}`}>
              <span className="audit-when muted small">{when(e.occurredAt)}</span>
              <span className="audit-what">
                {e.kind === "blocked" && leftForYou(e.note) ? (
                  <>
                    Stopped — needs you:
                    <ul className="left-for-you">{leftForYou(e.note)!.map((it) => <li key={it}>{it}</li>)}</ul>
                  </>
                ) : e.text}
                {e.fieldValue && e.kind !== "file_uploaded" && (
                  e.isSensitive && !showSensitive
                    ? <span className="muted"> — <button className="step-link small" type="button" onClick={() => setShowSensitive(true)}>show the answer</button></span>
                    : <span className="audit-value"> — “{e.fieldValue}”</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
      {open && events !== null && events.some((e) => e.isSensitive) && showSensitive && (
        <p className="muted small">Answers to legally operative questions come only from what you confirmed. <button className="step-link small" type="button" onClick={() => setShowSensitive(false)}>Hide them again</button></p>
      )}
    </div>
  );
}

/** What the collapsed row says about where this application stands, in one line, and its chip. */
export function rowSummary(app: ApplicationView, now: number = Date.now()): { line: string; chip: string; tone: "ok" | "due" | "muted" } {
  const s = app.submission;
  const unconfirmed = s.state === "in_progress" && Date.parse(app.updatedAt) < now - UNCONFIRMED_AFTER_MS;
  const humanCheck = s.state === "needs_input" && /human check|not a robot|captcha/i.test(s.failureDetail ?? "");
  const flagged = s.state === "needs_input" && !humanCheck && /possible spam|calling it spam/i.test(s.failureDetail ?? "");
  const items = s.state === "needs_input" ? leftForYou(s.failureDetail) : null;
  const due = Boolean(app.followUpOn && app.followUpOn <= todayIso() && BUCKET_OF[app.stage] !== "over");
  const stageLabel = app.stage.charAt(0).toUpperCase() + app.stage.slice(1);
  let line: string;
  let chip: string;
  let tone: "ok" | "due" | "muted";
  if (s.state === "submitted") {
    line = `Sent by JobCork · ${when(s.submittedAt)}`; chip = stageLabel === "Saved" ? "Sent" : stageLabel; tone = "ok";
  } else if (unconfirmed) {
    line = "Submit was pressed and the page did not confirm — say which here"; chip = "Sent? — unconfirmed"; tone = "due";
  } else if (humanCheck) {
    line = "Filled from the cloud, then a robot check at Submit — finish it in Chrome"; chip = "Needs you"; tone = "due";
  } else if (flagged) {
    line = "The form refuses machine sends — finish it in Chrome, you press Submit"; chip = "Needs you"; tone = "due";
  } else if (items && items.length > 0) {
    const names = items.map((it) => splitLeft(it).question).map((q) => (q.length > 48 ? `${q.slice(0, 46).trim()}…` : q));
    line = `${items.length} question${items.length === 1 ? "" : "s"} for you: ${names.slice(0, 2).join("; ")}${names.length > 2 ? `; +${names.length - 2} more` : ""}`;
    chip = "Needs you"; tone = "due";
  } else if (s.state === "needs_input") {
    // The form said a field JobCork filled was missing: the fill's own miss, tried again by Auto once a day (queue.ts gaveUp).
    const ownMiss = /Submit was pressed/i.test(s.failureDetail ?? "") && /needs corrections|Missing entry for required field/i.test(s.failureDetail ?? "") && s.attempts < 4;
    line = `${(s.failureDetail ?? "Stopped — needs you").slice(0, 120)}${ownMiss ? ` — Auto tries again tomorrow (try ${Math.min(s.attempts + 1, 4)} of 4)` : ""}`;
    chip = ownMiss ? "Retrying" : "Needs you"; tone = ownMiss ? "muted" : "due";
  } else if (s.state === "failed") {
    const d = (s.failureDetail ?? "").replace(/^JobCork could not find the application form on this page, or the fill did not finish\.?\s*/, "").trim();
    // JobCork's own give-up (the runner's minutes ran out, a stalled fill) is tried again by Auto once a day, up to four times (queue.ts gaveUp); the row says so instead of "failed" for good.
    const gaveUp = /the fill did not finish|Last step it reached|ran out of time|No application form recognised/i.test(s.failureDetail ?? "");
    const again = gaveUp && s.attempts < 4 ? ` — Auto tries again tomorrow (try ${Math.min(s.attempts + 1, 4)} of 4)` : "";
    line = `Nothing was sent${d ? ` — ${d.slice(0, 100)}` : " — the fill did not finish"}${again}`; chip = gaveUp && s.attempts < 4 ? "Retrying" : "Failed"; tone = gaveUp && s.attempts < 4 ? "muted" : "due";
  } else if (s.state === "in_progress") {
    line = "JobCork is filling the form now"; chip = "Filling"; tone = "muted";
  } else if (s.state === "queued") {
    line = "In Auto's queue — goes out on its next pass"; chip = "Queued"; tone = "muted";
  } else if (s.state === "abandoned") {
    line = "Skipped by Auto"; chip = "Skipped"; tone = "muted";
  } else {
    const applied = daysAgo(app.appliedAt);
    line = app.stage === "saved" ? `Saved ${daysAgo(app.createdAt) ?? ""}`.trim() : applied ? `Applied ${applied}` : stageLabel;
    chip = stageLabel; tone = BUCKET_OF[app.stage] === "in-play" ? "ok" : "muted";
  }
  if (due) line = `Follow-up due · ${line}`;
  if (app.job.closedAt) line = `Closed at the employer · ${line}`;
  return { line, chip, tone };
}

/**
 * Every needs-you row back in the queue at once (Steven, 19 Sept: 43 rows,
 * most stopped by a rule since fixed). A row stopped at a robot check is
 * left out -- it is finished in Chrome, not by another cloud run -- and so
 * is one the record says is being filled now. Each requeue is the same call
 * a row's own button makes, in turn, so the API's rules hold for each.
 */
function RetryAll({ rows, onDone }: { rows: ApplicationView[]; onDone: () => void }) {
  const [busy, setBusy] = useState<{ done: number; of: number } | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const eligible = rows.filter((a) => a.submission.state === "needs_input" && !/human check|not a robot|captcha|possible spam|calling it spam/i.test(a.submission.failureDetail ?? ""));
  if (eligible.length === 0) return null;
  const run = async () => {
    setBusy({ done: 0, of: eligible.length });
    setResult(null);
    let ok = 0;
    const failed: string[] = [];
    for (const [i, a] of eligible.entries()) {
      try {
        await api(`/api/applications/${a.jobId}/requeue`, { method: "POST", body: JSON.stringify({ note: "with every needs-you row, by you" }) });
        ok++;
      } catch (e) {
        failed.push(`${a.job.employer}: ${describe(e)}`);
      }
      setBusy({ done: i + 1, of: eligible.length });
    }
    setBusy(null);
    setResult(`${ok} of ${eligible.length} back in the queue${failed.length > 0 ? ` — not these: ${failed.slice(0, 3).join("; ")}${failed.length > 3 ? ` and ${failed.length - 3} more` : ""}` : ""}. Auto works through them on its next passes; anything a rule still cannot answer comes back here with the question.`);
    onDone();
  };
  return (
    <div className="row" style={{ alignItems: "baseline", gap: "0.75rem", marginBottom: "0.5rem" }}>
      <button className="btn small" type="button" disabled={busy !== null} onClick={() => void run()}>
        {busy ? `Queuing ${busy.done} of ${busy.of}…` : `Try all ${eligible.length} again`}
      </button>
      <span className="muted small">{result ?? `Puts all ${eligible.length} of these back in Auto's queue at once, so it fills them again with today's rules. ${rows.length > eligible.length ? `The other ${rows.length - eligible.length} stopped at a robot check: those you finish in Chrome.` : ""}`}</span>
    </div>
  );
}

function ApplicationRow({
  app,
  stages,
  onChanged,
  onRemoved,
  onTailor,
  autoMode,
  onRefresh,
  ext,
  onAnswerLeft,
  open,
  onToggle,
}: {
  app: ApplicationView;
  stages: ApplicationsResponse["stages"];
  onChanged: (a: ApplicationView) => void;
  onRemoved: () => void;
  onTailor: () => void;
  autoMode: "auto" | "review";
  onRefresh: () => void;
  ext: ExtensionStatus | null;
  onAnswerLeft?: (jobId: string) => void;
  /** Whether this row is the one opened to its details. */
  open: boolean;
  onToggle: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notes, setNotes] = useState(app.notes ?? "");
  const [savedTick, setSavedTick] = useState(false);

  const save = (patch: Record<string, unknown>) => {
    setBusy(true);
    setErr(null);
    setSavedTick(false);
    return api<{ application: ApplicationView }>(`/api/applications/${app.jobId}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    })
      .then((r) => { onChanged(r.application); setSavedTick(true); })
      .catch((e) => setErr(describe(e)))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    setBusy(true);
    setErr(null);
    api<{ removed: boolean }>(`/api/applications/${app.jobId}`, { method: "DELETE" })
      .then(onRemoved)
      .catch((e) => setErr(describe(e)))
      .finally(() => setBusy(false));
  };

  const applied = daysAgo(app.appliedAt);
  const pay = payLabel(app.job.comp);
  const followUpDue = Boolean(app.followUpOn && app.followUpOn <= todayIso() && BUCKET_OF[app.stage] !== "over");
  const summary = rowSummary(app);
  const s = app.submission;
  const leftItems = s.state === "needs_input" ? leftForYou(s.failureDetail) : null;
  const finishInChrome = s.state === "needs_input" && /human check|not a robot|captcha|possible spam|calling it spam/i.test(s.failureDetail ?? "") && !app.job.closedAt && !!app.job.applyUrl;
  const meta = [
    app.job.employer,
    app.job.isRemote ? "Remote" : app.job.place,
    pay,
    daysAgo(app.job.postedAt) ? `posted ${daysAgo(app.job.postedAt)}` : null,
    app.stage === "saved" ? (daysAgo(app.createdAt) ? `saved ${daysAgo(app.createdAt)}` : null) : applied ? `applied ${applied}` : null,
  ].filter(Boolean).join(" · ");
  const onKey = (e: ReactKeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } };

  return (
    <li className={`match app-row${open ? " open" : ""}`} id={`application-${app.jobId}`}>
      <div className="app-summary" role="button" tabIndex={0} aria-expanded={open} onClick={onToggle} onKeyDown={onKey}>
        <div className="app-main">
          <h3 className="tight">{app.job.title}</h3>
          <p className="muted small nomargin">{meta}</p>
          <p className={`small nomargin app-line${summary.tone === "due" ? " app-line-due" : ""}`}>{summary.line}</p>
        </div>
        <div className="app-side" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {leftItems && leftItems.length > 0 && onAnswerLeft && !finishInChrome && (
            <button className="btn small" type="button" onClick={() => onAnswerLeft(app.jobId)}>Answer what's left ({leftItems.length})</button>
          )}
          {finishInChrome && (
            <a className="btn small" href={app.job.applyUrl!} target="_blank" rel="noreferrer noopener">Finish in Chrome →</a>
          )}
          <span className={`badge app-chip${summary.tone === "due" ? " due" : summary.tone === "muted" ? " muted-badge" : ""}`}>{summary.chip}</span>
        </div>
        <span className="app-chev" aria-hidden="true">▾</span>
      </div>

      {open && (
      <div className="app-detail">
      <div className="match-head">
        <div>
          {app.stage === "saved" && (
            <p className="tags"><SendTag applyUrl={app.job.applyUrl} /></p>
          )}
        </div>
        <label className="stage">
          <span className="cap">Stage</span>
          <select
            className="input"
            value={app.stage}
            disabled={busy}
            onChange={(e) => save({ stage: e.target.value })}
          >
            {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      </div>

      {app.job.closedAt && (
        <p className="notice small">
          This posting has closed at the employer. Your record stays.
        </p>
      )}
      {followUpDue && <p className="badge due">Follow-up due</p>}

      {app.submission.state !== "none" && <Submission app={app} autoMode={autoMode} onChanged={onChanged} onRefresh={onRefresh} onAnswerLeft={onAnswerLeft} />}

      <div className="pair">
        <div className="field">
          <label className="field-label" htmlFor={`notes-${app.applicationId}`}>Notes</label>
          <textarea
            id={`notes-${app.applicationId}`}
            className="input notes"
            rows={3}
            value={notes}
            placeholder="Who you spoke to, what they said, what to do next."
            disabled={busy}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => { if ((notes.trim() || null) !== (app.notes ?? null)) void save({ notes }); }}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor={`follow-${app.applicationId}`}>Follow up on</label>
          <input
            id={`follow-${app.applicationId}`}
            className="input"
            type="date"
            value={app.followUpOn ?? ""}
            disabled={busy}
            onChange={(e) => save({ followUpOn: e.target.value || null })}
          />
          <p className="field-hint muted small">Shows as due from that day until you move the stage on.</p>
        </div>
      </div>

      <People jobId={app.jobId} people={app.people ?? []} onChanged={(people) => onChanged({ ...app, people })} />

      {BUCKET_OF[app.stage] === "in-play" && !app.job.closedAt && (
        <FollowUpDraft jobId={app.jobId} people={app.people ?? []} onSent={(followUpOn) => onChanged({ ...app, followUpOn })} />
      )}

      {/* They want to talk. This is the moment the whole pipeline was for. */}
      {["screening", "interviewing", "offer"].includes(app.stage) && (
        <InterviewPrep jobId={app.jobId} stage={app.stage} title={app.job.title} employer={app.job.employer} />
      )}

      <PostingReader jobId={app.jobId} />

      <div className="row">
        {app.job.applyUrl ? (
          <a className="btn ghost" href={app.job.applyUrl} target="_blank" rel="noreferrer noopener">
            Open the posting
          </a>
        ) : (
          <span className="muted small">The posting is no longer in the listings.</span>
        )}
        {app.job.applyUrl && (
          <button className="btn ghost" onClick={onTailor}>Tailor my resume</button>
        )}
        {app.stage === "saved" && app.submission.state !== "submitted" && app.submission.state !== "in_progress" && !app.job.closedAt && (
          <Send jobId={app.jobId} applyUrl={app.job.applyUrl} title={app.job.title} employer={app.job.employer} autoMode={autoMode} onOver={onRefresh} />
        )}
        {app.stage === "saved" && (
          <ConfirmRemove onRemove={remove} busy={busy} className="step-link" />
        )}
        {savedTick && !busy && !err && <span className="saved small">Saved</span>}
        {err && <span className="err small">{err}</span>}
      </div>
      {BUCKET_OF[app.stage] !== "over" && app.stage === "saved" && app.submission.state !== "submitted" && !app.job.closedAt && <FillHint applyUrl={app.job.applyUrl} ext={ext} autoMode={autoMode} />}
      </div>
      )}
    </li>
  );
}
